import { api } from "../../../api/Middleware";
import { setCookie } from "nookies";
import {
  ORGANIZATIONS_URL,
  ORGANIZATIONS_ANALYTICS_URL,
  ORGANIZATIONS_UPDATE_BRANDING_URL,
  ORGANIZATIONS_BRANDING_URL,
  ORGANIZATIONS_PREVIEW_BRANDING_URL,
  ORGANIZATIONS_SUBACCOUNTS_URL,
  ORGANIZATIONS_SMTP_URL,
  ORGANIZATIONS_TEST_SMTP_URL,
} from "../../../api/Urls";

const OrganizationService = {
  // Criar organização
  createOrganization: async (data) => {
    try {
      const response = await api.post(ORGANIZATIONS_URL, data);
      return response.data;
    } catch (error) {
      console.error("Error creating organization:", error);
      throw error;
    }
  },

  // Listar organizações do usuário
  getOrganizations: async (params = {}) => {
    try {
      const response = await api.get(ORGANIZATIONS_URL, { params });
      return response.data;
    } catch (error) {
      console.error("Error fetching organizations:", error);
      throw error;
    }
  },

  // Obter organização específica
  getOrganization: async (id) => {
    try {
      const response = await api.get(`${ORGANIZATIONS_URL}${id}/`);
      return response.data;
    } catch (error) {
      console.error("Error fetching organization:", error);
      throw error;
    }
  },

  // Atualizar organização
  updateOrganization: async (id, data) => {
    try {
      const response = await api.patch(`${ORGANIZATIONS_URL}${id}/`, data);
      return response.data;
    } catch (error) {
      console.error("Error updating organization:", error);
      throw error;
    }
  },

  // Verificar disponibilidade de domínio customizado
  checkDomainAvailability: async (domain) => {
    try {
      const response = await api.get(`${ORGANIZATIONS_URL}check_domain/`, {
        params: { domain },
      });
      return response.data;
    } catch (error) {
      if (error.response?.status === 404) {
        return { available: true };
      }
      throw error;
    }
  },

  // ========================================
  // NOVOS MÉTODOS PARA BRANDING
  // ========================================

  // Obter configurações de branding da organização
  getOrganizationBranding: async (id) => {
    try {
      const response = await api.get(ORGANIZATIONS_BRANDING_URL(id));
      return response.data.branding_config || response.data;
    } catch (error) {
      console.error("Error fetching organization branding:", error);
      throw error;
    }
  },

  // Atualizar branding da organização (método expandido)
  updateBranding: async (id, data, hasExistingBranding = false) => {
    try {
      let requestData;
      let headers = {};

      // Verificar se é FormData (com arquivos) ou objeto simples
      if (data instanceof FormData) {
        requestData = data;
        headers["Content-Type"] = "multipart/form-data";
      } else {
        // Se for objeto simples, converter para FormData se necessário
        requestData = new FormData();

        // Adicionar campos de texto (excluir campos de arquivo que não são File objects)
        Object.keys(data).forEach((key) => {
          if (
            data[key] !== null &&
            data[key] !== undefined &&
            !(data[key] instanceof File) &&
            !['logo', 'logo_mobile', 'favicon', 'social_banner'].includes(key)
          ) {
            requestData.append(key, data[key]);
          }
        });

        // Adicionar apenas arquivos novos (File objects), não URLs existentes
        if (data.logo instanceof File) {
          requestData.append("logo", data.logo);
        }
        if (data.logo_mobile instanceof File) {
          requestData.append("logo_mobile", data.logo_mobile);
        }
        if (data.favicon instanceof File) {
          requestData.append("favicon", data.favicon);
        }
        if (data.social_banner instanceof File) {
          requestData.append("social_banner", data.social_banner);
        }

        headers["Content-Type"] = "multipart/form-data";
      }

      // Usar PATCH se já existe branding, POST se é novo
      const method = hasExistingBranding ? 'patch' : 'post';
      const response = await api[method](
        ORGANIZATIONS_UPDATE_BRANDING_URL(id),
        requestData,
        { headers }
      );

      return response.data;
    } catch (error) {
      console.error("Error updating branding:", error);
      throw error;
    }
  },

  // Preview de branding com mudanças temporárias
  previewBranding: async (id, tempChanges = {}) => {
    try {
      const params = new URLSearchParams();

      // Adicionar mudanças temporárias como query params
      Object.keys(tempChanges).forEach((key) => {
        if (tempChanges[key]) {
          params.append(key, tempChanges[key]);
        }
      });

      const response = await api.get(
        `${ORGANIZATIONS_PREVIEW_BRANDING_URL(id)}?${params.toString()}`
      );

      return response.data;
    } catch (error) {
      console.error("Error previewing branding:", error);
      throw error;
    }
  },

  // Validar dados de branding
  validateBrandingData: (data) => {
    const errors = {};

    // Validar cores em formato hex
    const colorFields = [
      "primary_color",
      "secondary_color",
      "accent_color",
      "background_color",
    ];
    colorFields.forEach((field) => {
      if (data[field] && !/^#[0-9A-F]{6}$/i.test(data[field])) {
        errors[field] = "Cor deve estar no formato #RRGGBB";
      }
    });

    // Validar domínio customizado se fornecido
    if (data.custom_domain) {
      const domainRegex =
        /^[a-zA-Z0-9][a-zA-Z0-9-]{0,61}[a-zA-Z0-9](?:\.[a-zA-Z]{2,})+$/;
      if (!domainRegex.test(data.custom_domain)) {
        errors.custom_domain = "Formato de domínio inválido";
      }
    }

    // Validar subdomínio se fornecido
    if (data.subdomain) {
      const subdomainRegex = /^[a-zA-Z0-9][a-zA-Z0-9-]{0,61}[a-zA-Z0-9]$/;
      if (!subdomainRegex.test(data.subdomain)) {
        errors.subdomain =
          "Formato de subdomínio inválido (apenas letras, números e hífen)";
      }
    }

    return {
      isValid: Object.keys(errors).length === 0,
      errors,
    };
  },

  // Gerar CSS customizado baseado no branding
  generateCustomCSS: (brandingConfig) => {
    if (!brandingConfig) return "";

    const colors = brandingConfig.colors || {};

    return `
      :root {
        /* Sobrescrever variáveis principais */
        --primary: ${colors.primary || "#3b82f6"};
        --gradient-primary: ${
          colors.gradient_primary ||
          "var(--gradient-primary)"
        };
        --text-primary: ${colors.titles || "#0f172a"};
        --text-secondary: ${colors.text || "#475569"};
        --accent: ${colors.accent || "#10b981"};
        --background: ${colors.background || "#ffffff"};
        
        /* Variáveis whitelabel */
        --whitelabel-primary: ${colors.primary || "#3b82f6"};
        --whitelabel-background: ${colors.background || "#ffffff"};
        
        /* Variáveis organizacionais específicas */
        --org-brand-name: '${brandingConfig.brand_name || ""}';
      }
    `;
  },

  // Aplicar branding customizado na página
  applyCustomBranding: (brandingConfig) => {
    if (!brandingConfig) return;

    // Aplicar CSS customizado
    const customCSS = OrganizationService.generateCustomCSS(brandingConfig);

    // Remover CSS anterior se existir
    const existingStyle = document.getElementById("custom-org-branding");
    if (existingStyle) {
      existingStyle.remove();
    }

    // Adicionar novo CSS
    const styleElement = document.createElement("style");
    styleElement.id = "custom-org-branding";
    styleElement.textContent = customCSS;
    document.head.appendChild(styleElement);

    // Atualizar título da página se necessário
    if (brandingConfig.brand_name) {
      document.title = `${brandingConfig.brand_name} - Dashboard`;
    }

    // Atualizar favicon se fornecido
    if (brandingConfig.logo?.favicon) {
      const favicon =
        document.querySelector('link[rel="icon"]') ||
        document.createElement("link");
      favicon.rel = "icon";
      favicon.href = brandingConfig.logo.favicon;
      if (!document.querySelector('link[rel="icon"]')) {
        document.head.appendChild(favicon);
      }
    }

    // Armazenar no localStorage para persistência
    try {
      localStorage.setItem("org-branding", JSON.stringify(brandingConfig));
    } catch (error) {
      console.warn("Could not save branding to localStorage:", error);
    }
  },

  // Carregar branding do localStorage
  loadStoredBranding: () => {
    try {
      const stored = localStorage.getItem("org-branding");
      if (stored) {
        const brandingConfig = JSON.parse(stored);
        OrganizationService.applyCustomBranding(brandingConfig);
        return brandingConfig;
      }
    } catch (error) {
      console.warn("Could not load branding from localStorage:", error);
    }
    return null;
  },

  // Remover branding customizado
  removeCustomBranding: () => {
    // Remover CSS customizado
    const existingStyle = document.getElementById("custom-org-branding");
    if (existingStyle) {
      existingStyle.remove();
    }

    // Limpar localStorage
    try {
      localStorage.removeItem("org-branding");
    } catch (error) {
      console.warn("Could not remove branding from localStorage:", error);
    }

    // Restaurar título padrão
    document.title = "Mercado Agentes - Dashboard";
  },

  // Atualizar cache do WhitelabelContext após atualização de branding
  updateWhitelabelCache: (apiResponse) => {
    try {
      const CACHE_KEY = "whitelabel_config";
      
      // Obter configuração atual do cache
      const cached = localStorage.getItem(CACHE_KEY);
      if (!cached) {
        return false;
      }

      const currentCache = JSON.parse(cached);
      
      // Verificar se a resposta da API tem o branding_config
      if (!apiResponse || !apiResponse.branding_config) {
        // FALLBACK: Se não tem branding_config, tentar usar a organização diretamente
        if (apiResponse.organization && apiResponse.organization.branding_config) {
          apiResponse.branding_config = apiResponse.organization.branding_config;
        } else {
          return false;
        }
      }

      // Guard: só atualiza o cache global de whitelabel quando a
      // organização TEM whitelabel ativo (white_label_enabled === true).
      // Sem isso, salvar uma logo personalizada numa conta não-whitelabel
      // sobrescreveria o branding padrão da Mercado Agentes na sidebar —
      // exatamente o sintoma do bug "logo sumiu".
      if (apiResponse.branding_config.white_label_enabled !== true) {
        return false;
      }

      // Atualizar os dados do cache com os novos valores da API.
      // is_whitelabel top-level reflete o flag mais recente do response.
      const updatedData = {
        ...currentCache.data,
        is_whitelabel: true,
        branding: {
          ...(currentCache.data.branding || {}),
          // Atualizar brand_name se veio na resposta
          brand_name:
            apiResponse.branding_config.brand_name ||
            currentCache.data.branding?.brand_name,
          
          // Atualizar logos com os novos URLs da API
          logo: {
            ...(currentCache.data.branding?.logo || {}),
            main:
              apiResponse.branding_config.logo?.main ||
              currentCache.data.branding?.logo?.main,
            mobile:
              apiResponse.branding_config.logo?.mobile ||
              currentCache.data.branding?.logo?.mobile,
            favicon:
              apiResponse.branding_config.logo?.favicon ||
              currentCache.data.branding?.logo?.favicon,
          },

          // Atualizar cores
          colors: {
            ...(currentCache.data.branding?.colors || {}),
            ...(apiResponse.branding_config.colors || {}),
          },

          // Atualizar domínio
          domain: {
            ...(currentCache.data.branding?.domain || {}),
            ...(apiResponse.branding_config.domain || {}),
          },

          // Atualizar SEO
          seo: {
            ...(currentCache.data.branding?.seo || {}),
            ...(apiResponse.branding_config.seo || {}),
          },

          // Atualizar tokens de sidebar (menu lateral)
          sidebar: {
            ...(currentCache.data.branding?.sidebar || {}),
            ...(apiResponse.branding_config.sidebar || {}),
          },

          // Atualizar CSS customizations
          css_customizations: {
            ...(currentCache.data.branding?.css_customizations || {}),
            ...(apiResponse.branding_config.css_customizations || {}),
          },

          // Atualizar white_label_enabled
          white_label_enabled:
            apiResponse.branding_config.white_label_enabled,
        },
      };

      // Manter timestamp original ou atualizar para agora
      const updatedCache = {
        data: updatedData,
        timestamp: new Date().getTime()
      };

      // Salvar cache atualizado
      localStorage.setItem(CACHE_KEY, JSON.stringify(updatedCache));
      
      return true;
      
    } catch (error) {
      console.error("❌ Erro ao atualizar cache whitelabel:", error);
      return false;
    }
  },

  // ========================================
  // MÉTODOS PARA SMTP WHITE LABEL
  // ========================================

  // Obter configuração SMTP da organização (senha mascarada)
  getOrganizationSMTP: async (id) => {
    try {
      const response = await api.get(ORGANIZATIONS_SMTP_URL(id));
      return response.data;
    } catch (error) {
      console.error("Error fetching SMTP config:", error);
      throw error;
    }
  },

  // Atualizar configuração SMTP (backend valida via teste antes de salvar)
  updateOrganizationSMTP: async (id, data) => {
    try {
      const response = await api.patch(ORGANIZATIONS_SMTP_URL(id), data);
      return response.data;
    } catch (error) {
      console.error("Error updating SMTP config:", error);
      throw error;
    }
  },

  // Testar configuração SMTP sem salvar
  testSMTP: async (id, data) => {
    try {
      const response = await api.post(ORGANIZATIONS_TEST_SMTP_URL(id), data);
      return response.data;
    } catch (error) {
      // Erros 400 carregam o resultado do teste no response.data
      if (error?.response?.data) {
        return error.response.data;
      }
      console.error("Error testing SMTP:", error);
      throw error;
    }
  },

  // ========================================
  // MÉTODOS EXISTENTES (mantidos)
  // ========================================

  // Obter analytics da organização
  getAnalytics: async (id) => {
    try {
      const response = await api.get(ORGANIZATIONS_ANALYTICS_URL(id));
      return response.data;
    } catch (error) {
      console.error("Error fetching analytics:", error);
      throw error;
    }
  },

  // Obter clientes da organização
  getSubaccounts: async (id, params = {}) => {
    try {
      const response = await api.get(ORGANIZATIONS_SUBACCOUNTS_URL(id), {
        params,
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching subaccounts:", error);
      throw error;
    }
  },

  // Deletar organização
  deleteOrganization: async (id) => {
    try {
      const response = await api.delete(`${ORGANIZATIONS_URL}${id}/`);
      return response.data;
    } catch (error) {
      console.error("Error deleting organization:", error);
      throw error;
    }
  },

  // Validar dados da organização antes de enviar
  validateOrganizationData: (data) => {
    const errors = {};

    if (!data.name || data.name.trim().length < 3) {
      errors.name = "Nome deve ter pelo menos 3 caracteres";
    }

    if (!data.brand_name || data.brand_name.trim().length < 2) {
      errors.brand_name = "Nome da marca deve ter pelo menos 2 caracteres";
    }

    // Validar domínio customizado se fornecido
    if (data.custom_domain) {
      const domainRegex =
        /^[a-zA-Z0-9][a-zA-Z0-9-]{0,61}[a-zA-Z0-9](?:\.[a-zA-Z]{2,})+$/;
      if (!domainRegex.test(data.custom_domain)) {
        errors.custom_domain = "Formato de domínio inválido";
      }
    }

    return {
      isValid: Object.keys(errors).length === 0,
      errors,
    };
  },

  // Preview da URL da organização
  previewOrganizationUrl: (customDomain) => {
    if (customDomain) {
      return `https://${customDomain}`;
    }
    return "";
  },

  // ========================================
  // UTILITÁRIOS PARA UPLOAD DE ARQUIVOS
  // ========================================

  // Validar arquivo de imagem
  validateImageFile: (file, type = "logo") => {
    const errors = [];

    // Validar tipos permitidos
    const allowedTypes = {
      logo: ["image/jpeg", "image/png", "image/svg+xml"],
      logo_mobile: ["image/jpeg", "image/png", "image/svg+xml"],
      favicon: ["image/x-icon", "image/png"],
    };

    if (!allowedTypes[type].includes(file.type)) {
      errors.push(
        `Formato não suportado. Use: ${allowedTypes[type].join(", ")}`
      );
    }

    // Validar tamanho
    const maxSizes = {
      logo: 2 * 1024 * 1024, // 2MB
      logo_mobile: 2 * 1024 * 1024, // 2MB
      favicon: 100 * 1024, // 100KB
    };

    if (file.size > maxSizes[type]) {
      const maxSizeText = type === "favicon" ? "100KB" : "2MB";
      errors.push(`Arquivo muito grande. Máximo: ${maxSizeText}`);
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  },

  // Redimensionar imagem antes do upload (usando canvas)
  resizeImage: (file, maxWidth, maxHeight, quality = 0.8) => {
    return new Promise((resolve) => {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      const img = new Image();

      img.onload = () => {
        // Calcular novas dimensões mantendo proporção
        let { width, height } = img;

        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }

        if (height > maxHeight) {
          width = (width * maxHeight) / height;
          height = maxHeight;
        }

        // Redimensionar no canvas
        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(img, 0, 0, width, height);

        // Converter para blob
        canvas.toBlob(resolve, file.type, quality);
      };

      img.src = URL.createObjectURL(file);
    });
  },

  // Gerar preview de imagem
  generateImagePreview: (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (e) => resolve(e.target.result);
      reader.onerror = reject;

      reader.readAsDataURL(file);
    });
  },

  // ========================================
  // TEMPLATES DE BRANDING PREDEFINIDOS
  // ========================================

  // Templates de cores predefinidos
  getBrandingTemplates: () => {
    return [
      {
        name: "Azul Profissional",
        colors: {
          primary_color: "#3b82f6",
          secondary_color: "#6b7280",
          accent_color: "#10b981",
          background_color: "#ffffff",
        },
      },
      {
        name: "Verde Sucesso",
        colors: {
          primary_color: "#10b981",
          secondary_color: "#6b7280",
          accent_color: "#3b82f6",
          background_color: "#ffffff",
        },
      },
      {
        name: "Roxo Criativo",
        colors: {
          primary_color: "#8b5cf6",
          secondary_color: "#6b7280",
          accent_color: "#06b6d4",
          background_color: "#ffffff",
        },
      },
      {
        name: "Laranja Energético",
        colors: {
          primary_color: "#f59e0b",
          secondary_color: "#6b7280",
          accent_color: "#ef4444",
          background_color: "#ffffff",
        },
      },
      {
        name: "Rosa Moderno",
        colors: {
          primary_color: "#ec4899",
          secondary_color: "#6b7280",
          accent_color: "#8b5cf6",
          background_color: "#ffffff",
        },
      },
      {
        name: "Escuro Elegante",
        colors: {
          primary_color: "#1f2937",
          secondary_color: "#9ca3af",
          accent_color: "#3b82f6",
          background_color: "#f9fafb",
        },
      },
    ];
  },

  // Aplicar template de branding
  applyBrandingTemplate: (template) => {
    return {
      ...template.colors,
      brand_name: "", // Será preenchido pelo usuário
      custom_domain: "",
      subdomain: "",
    };
  },

  // ========================================
  // MÉTODOS PARA ACESSO À CONTA DO CLIENTE
  // ========================================

  // Iniciar acesso à conta do cliente
  accessClientAccount: async (orgId, subaccountId, reason = "Suporte técnico", durationHours = 4) => {
    try {
      const response = await api.post(`${ORGANIZATIONS_URL}${orgId}/access_client_account/`, {
        subaccount_id: subaccountId,
        reason,
        duration_hours: durationHours
      });
      return response.data;
    } catch (error) {
      console.error("Error accessing client account:", error);
      throw error;
    }
  },

  // Retornar à conta original
  returnToOriginalAccount: async (switchSessionId) => {
    try {
      const response = await api.post(`${ORGANIZATIONS_URL}return_to_original_account/`, {
        switch_session_id: switchSessionId
      });

      // CORREÇÃO: Usar o token NOVO retornado pela API, não o antigo
      if (response.data.success && response.data.original_access_token) {
        // Atualizar com o token NOVO gerado pela API
        localStorage.setItem('token', response.data.original_access_token);
        setCookie(null, 'token', response.data.original_access_token, {
          maxAge: 30 * 24 * 60 * 60, // 30 dias
          path: '/'
        });
      }

      return response.data;
    } catch (error) {
      console.error("Error returning to original account:", error);
      throw error;
    }
  },

  // Listar sessões ativas (Admin)
  getActiveClientSessions: async (orgId) => {
    try {
      const response = await api.get(`${ORGANIZATIONS_URL}${orgId}/active_client_sessions/`);
      return response.data;
    } catch (error) {
      console.error("Error fetching active client sessions:", error);
      throw error;
    }
  },

  // Encerrar sessão (Admin)
  endClientSession: async (orgId, sessionId, reason = "Encerramento administrativo") => {
    try {
      const response = await api.post(`${ORGANIZATIONS_URL}${orgId}/end_client_session/`, {
        session_id: sessionId,
        reason
      });
      return response.data;
    } catch (error) {
      console.error("Error ending client session:", error);
      throw error;
    }
  },

  // Verificar se usuário pode acessar conta do cliente
  canAccessClientAccount: (user, subaccount) => {
    // Usuário deve ser Admin (superuser_level = 1) ou Gerente (superuser_level = 2)
    if (!user || user.superuser_level > 2) {
      return false;
    }

    // Subconta deve estar ativa
    if (!subaccount || subaccount.status !== 1) {
      return false;
    }

    return true;
  },

  // Gerenciar contexto de sessão ativa
  setActiveSession: (sessionData) => {
    try {
      localStorage.setItem('client_access_session', JSON.stringify({
        sessionId: sessionData.switch_session_id,
        originalToken: localStorage.getItem('token'), // Salvar token original
        clientToken: sessionData.client_access_token,
        clientAccount: sessionData.client_account,
        expiresAt: sessionData.session_info.expires_at,
        reason: sessionData.session_info.reason
      }));
      
      // Atualizar token atual para o token do cliente
      localStorage.setItem('token', sessionData.client_access_token);
      setCookie(null, 'token', sessionData.client_access_token, {
        maxAge: 8 * 60 * 60, // 8 horas
        path: '/'
      });
      
      return true;
    } catch (error) {
      console.error("Error setting active session:", error);
      return false;
    }
  },

  // Obter sessão ativa
  getActiveSession: () => {
    try {
      const session = localStorage.getItem('client_access_session');
      if (!session) return null;
      
      const sessionData = JSON.parse(session);
      
      // Verificar se expirou
      if (new Date(sessionData.expiresAt) <= new Date()) {
        OrganizationService.clearActiveSession();
        return null;
      }
      
      return sessionData;
    } catch (error) {
      console.error("Error getting active session:", error);
      return null;
    }
  },

  // Limpar sessão ativa
  clearActiveSession: () => {
    try {
      // CORREÇÃO: Apenas limpar a sessão local
      // O token correto já foi definido pela API em returnToOriginalAccount
      localStorage.removeItem('client_access_session');
      return true;
    } catch (error) {
      console.error("Error clearing active session:", error);
      return false;
    }
  },

  // Verificar se está em sessão de cliente
  isInClientSession: () => {
    const session = OrganizationService.getActiveSession();
    return session !== null;
  },

  // Obter informações da sessão atual
  getCurrentSessionInfo: () => {
    const session = OrganizationService.getActiveSession();
    if (!session) return null;

    const now = new Date();
    const expiresAt = new Date(session.expiresAt);
    const timeLeft = Math.max(0, expiresAt - now);
    
    return {
      ...session,
      timeLeftMinutes: Math.floor(timeLeft / (1000 * 60)),
      isExpiringSoon: timeLeft < 30 * 60 * 1000 // Menos de 30 minutos
    };
  }
};

export default OrganizationService;
